import { useEffect, useState } from "react";
import { X, Trash2, Loader2, AlertTriangle } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { EVENT_TYPES, toLocalInput, fromLocalInput } from "./agendaUtils";
import { useT } from "../../lib/i18n";

export default function EventModal({ event, initialStart, me, members, isAdmin, clients, contacts, onClose, onSaved }) {
  const t = useT();
  const isNew = !event?.id;
  const [form, setForm] = useState(() => ({
    title: event?.title || "",
    event_type: event?.event_type || "meeting",
    start: toLocalInput(event?.start || initialStart || new Date().toISOString()),
    end: toLocalInput(event?.end || ""),
    location: event?.location || "",
    notes: event?.notes || "",
    client_id: event?.client_id || "",
    contact_id: event?.contact_id || "",
    owner_user_id: event?.owner_user_id || me?.id || "",
  }));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const ownerIsMe = form.owner_user_id === me?.id;
  const ownerMember = members.find((m) => m.user_id === form.owner_user_id);
  const canWrite = ownerIsMe || isAdmin || ownerMember?.team_permission === "editor";
  const editableOwners = members.filter((m) => m.user_id === me?.id || isAdmin || m.team_permission === "editor");

  useEffect(() => { setError(""); }, [form]);

  const save = async () => {
    if (!form.title.trim() || !form.start) { setError(t("Titel en starttijd zijn verplicht.")); return; }
    setBusy(true);
    try {
      const payload = {
        title: form.title.trim(), event_type: form.event_type,
        start: fromLocalInput(form.start), end: fromLocalInput(form.end),
        location: form.location || null, notes: form.notes || null,
        client_id: form.client_id || null, contact_id: form.contact_id || null,
      };
      if (isNew) await api.post("/agenda/events", { ...payload, owner_user_id: form.owner_user_id || null });
      else await api.put(`/agenda/events/${event.id}`, payload);
      onSaved();
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const remove = async () => {
    setBusy(true);
    try { await api.delete(`/agenda/events/${event.id}`); onSaved(); }
    catch (e) { setError(formatApiErrorDetail(e?.response?.data?.detail)); setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="agenda-event-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[92vh] overflow-y-auto pm-scroll">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 sticky top-0 bg-white">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">{isNew ? t("Nieuwe afspraak") : t("Afspraak")}</div>
            <div className="font-display text-lg font-bold tracking-tight">{isNew ? t("Toevoegen aan agenda") : event.title}</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="agenda-modal-close"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-3">
          {!canWrite && (
            <div className="rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-xs px-3 py-2 flex items-center gap-2" data-testid="agenda-readonly-banner">
              <AlertTriangle size={12} /> {t("Alleen inzien — {x} heeft geen editor-rechten gedeeld.", { x: ownerMember?.name || t("deze collega") })}
            </div>
          )}
          {(event?.conflicts_with || []).length > 0 && (
            <div className="rounded-lg bg-red-50 border border-red-200 text-red-800 text-xs px-3 py-2" data-testid="agenda-conflict-banner">
              <b>{t("Conflict")}:</b> {event.conflicts_with.map((c) => `${c.owner_name} — ${c.title}`).join("; ")}
            </div>
          )}
          <label className="block text-xs font-bold text-slate-600">{t("Titel")}
            <input className="pm-input mt-1 w-full" value={form.title} onChange={(e) => set("title", e.target.value)} disabled={!canWrite} data-testid="agenda-form-title" placeholder={t("Call met Ajax over Delano")} />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block text-xs font-bold text-slate-600">{t("Type")}
              <select className="pm-input mt-1 w-full" value={form.event_type} onChange={(e) => set("event_type", e.target.value)} disabled={!canWrite} data-testid="agenda-form-type">
                {EVENT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </label>
            <label className="block text-xs font-bold text-slate-600">{t("Agenda van")}
              <select className="pm-input mt-1 w-full" value={form.owner_user_id} onChange={(e) => set("owner_user_id", e.target.value)} disabled={!isNew} data-testid="agenda-form-owner">
                {(isNew ? editableOwners : members).map((m) => <option key={m.user_id} value={m.user_id}>{m.name}{m.user_id === me?.id ? ` (${t("ik")})` : ""}</option>)}
              </select>
            </label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block text-xs font-bold text-slate-600">{t("Start")}
              <input type="datetime-local" className="pm-input mt-1 w-full" value={form.start} onChange={(e) => set("start", e.target.value)} disabled={!canWrite} data-testid="agenda-form-start" />
            </label>
            <label className="block text-xs font-bold text-slate-600">{t("Einde")}
              <input type="datetime-local" className="pm-input mt-1 w-full" value={form.end} onChange={(e) => set("end", e.target.value)} disabled={!canWrite} data-testid="agenda-form-end" />
            </label>
          </div>
          <label className="block text-xs font-bold text-slate-600">{t("Locatie")}
            <input className="pm-input mt-1 w-full" value={form.location} onChange={(e) => set("location", e.target.value)} disabled={!canWrite} data-testid="agenda-form-location" placeholder="Johan Cruijff ArenA / Teams" />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block text-xs font-bold text-slate-600">{t("Dossier (speler)")}
              <select className="pm-input mt-1 w-full" value={form.client_id} onChange={(e) => set("client_id", e.target.value)} disabled={!canWrite} data-testid="agenda-form-client">
                <option value="">{t("— geen —")}</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.full_name}</option>)}
              </select>
            </label>
            <label className="block text-xs font-bold text-slate-600">{t("Contact (club/agent)")}
              <select className="pm-input mt-1 w-full" value={form.contact_id} onChange={(e) => set("contact_id", e.target.value)} disabled={!canWrite} data-testid="agenda-form-contact">
                <option value="">{t("— geen —")}</option>
                {contacts.map((c) => <option key={c.id} value={c.id}>{c.full_name}{c.organisation ? ` · ${c.organisation}` : ""}</option>)}
              </select>
            </label>
          </div>
          <label className="block text-xs font-bold text-slate-600">{t("Notities")}
            <textarea className="pm-input mt-1 w-full min-h-[70px]" value={form.notes} onChange={(e) => set("notes", e.target.value)} disabled={!canWrite} data-testid="agenda-form-notes" />
          </label>
          {error && <div className="text-xs text-red-600" data-testid="agenda-form-error">{error}</div>}
          <div className="flex items-center gap-2 pt-2">
            {!isNew && canWrite && (
              <button onClick={remove} disabled={busy} className="pm-btn-secondary h-9 px-3 rounded-lg text-xs font-bold text-red-600 flex items-center gap-1" data-testid="agenda-form-delete"><Trash2 size={12} /> {t("Verwijderen")}</button>
            )}
            <div className="ml-auto flex gap-2">
              <button onClick={onClose} className="pm-btn-secondary h-9 px-3 rounded-lg text-xs font-bold" data-testid="agenda-form-cancel">{t("Annuleren")}</button>
              {canWrite && (
                <button onClick={save} disabled={busy} className="pm-btn-primary h-9 px-4 rounded-lg text-xs font-bold flex items-center gap-1" data-testid="agenda-form-save">
                  {busy && <Loader2 size={12} className="animate-spin" />} {isNew ? t("Toevoegen") : t("Opslaan")}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
